@file:Suppress("TooGenericExceptionThrown")

package com.klantenstop.web.data.repos

import com.klantenstop.web.data.NOT_FOUND_STATUS_CODE
import com.klantenstop.web.data.NetworkingConfig
import com.klantenstop.web.data.QueryOptions
import com.klantenstop.web.data.getRequest
import com.klantenstop.web.data.models.NetworkResponse
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.data.models.domain.toCompany
import com.klantenstop.web.data.models.dto.requests.company.InsertCompany
import com.klantenstop.web.data.models.dto.requests.company.UpdateCompany
import com.klantenstop.web.data.models.dto.responses.CompanyDto
import com.klantenstop.web.data.models.dto.responses.CompanyPinsDto
import com.klantenstop.web.data.models.dto.responses.CompanySearchResultDto
import com.klantenstop.web.data.models.dto.responses.ErrorResponse
import com.klantenstop.web.data.postRequest
import com.klantenstop.web.data.putRequest

private const val DEFAULT_PAGE_SIZE = 20

object CompanyRepo {
    private val companyUrl = "${NetworkingConfig.baseUrl}/api/v1/companies"

    // `citySlug` must already be in slugified form (see `toUrlSlug()` in :core), since the backend's
    // `stad` param is matched against curated-city/province slugs before falling back to geocoding.
    // Slugification lives in :core, which :data cannot depend on, so callers do it themselves.
    suspend fun getCompanies(
        categoryIdentifier: String?,
        citySlug: String?,
        radiusInKm: Int,
        page: Int = 1,
        pageSize: Int = DEFAULT_PAGE_SIZE
    ): CompanySearchResultDto {

        val queryOptions = QueryOptions(
            params = mapOf(
                "radius" to radiusInKm.toString(),
                "category_id" to (categoryIdentifier ?: ""),
                "stad" to (citySlug ?: ""),
                "page" to page.toString(),
                "pageSize" to pageSize.toString()
            )
        )

        val response: NetworkResponse<CompanySearchResultDto, ErrorResponse> = getRequest(companyUrl, queryOptions = queryOptions)

        return when (response) {
            is NetworkResponse.Success -> response.body
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: "Server fout bij het ophalen van bedrijven"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van bedrijven")
        }
    }

    // Unpaginated/soft-capped companion to `getCompanies`, used to populate map markers: the map
    // needs every matching company's pin, independent of the card list's current page.
    suspend fun getCompanyPins(
        categoryIdentifier: String?,
        citySlug: String?,
        radiusInKm: Int
    ): CompanyPinsDto {

        val queryOptions = QueryOptions(
            params = mapOf(
                "radius" to radiusInKm.toString(),
                "category_id" to (categoryIdentifier ?: ""),
                "stad" to (citySlug ?: "")
            )
        )

        val response: NetworkResponse<CompanyPinsDto, ErrorResponse> = getRequest("$companyUrl/pins", queryOptions = queryOptions)

        return when (response) {
            is NetworkResponse.Success -> response.body
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: "Server fout bij het ophalen van kaartmarkeringen"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van kaartmarkeringen")
        }
    }

    suspend fun getCompany(identifier: String): Company? {

        val response: NetworkResponse<CompanyDto, ErrorResponse> = getRequest("$companyUrl/$identifier")

        return when (response) {
            is NetworkResponse.Success -> response.body.toCompany()
            is NetworkResponse.ServerError -> {
                if (response.body?.status == NOT_FOUND_STATUS_CODE) {
                    null
                } else {
                    val errorMessage = response.body?.errorDescription ?: "Server fout bij het ophalen van bedrijf"
                    throw Exception(errorMessage)
                }
            }


            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het ophalen van bedrijf")
        }
    }

    suspend fun createCompany(insertCompany: InsertCompany): Company? {
        val response: NetworkResponse<CompanyDto, ErrorResponse> = postRequest(companyUrl, insertCompany)

        return when (response) {
            is NetworkResponse.Success -> response.body.toCompany()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: "Server fout bij het aanmaken van bedrijf"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het aanmaken van bedrijf")
        }
    }

    suspend fun updateCompany(id: String, updateCompany: UpdateCompany): Company? {
        val response: NetworkResponse<CompanyDto, ErrorResponse> = putRequest("$companyUrl/$id", updateCompany)

        return when (response) {
            is NetworkResponse.Success -> response.body.toCompany()
            is NetworkResponse.ServerError -> {
                val errorMessage = response.body?.errorDescription ?: "Server fout bij het bijwerken van bedrijf"
                throw Exception(errorMessage)
            }

            is NetworkResponse.NetworkError -> throw Exception("Netwerkfout: controleer je internetverbinding")
            is NetworkResponse.UnknownError -> throw Exception("Onbekende fout bij het bijwerken van bedrijf")
        }
    }
}
